/**
 * ProfileSelfModal — лайтбокс редактирования СВОЕГО профиля на messenger PWA.
 * Три вкладки: Основное / Оформление / Настройки.
 * Стилизован в духе messenger redesign (white card, accent green, sans-serif).
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Avatar } from "@/components/prototype/Avatar";
import { Icon } from "@/components/prototype/Icon";
import { useAuth } from "@/auth/AuthContext";
import {
  uploadMyAvatar, deleteMyAvatar, updateMyProfile,
  fetchTelegramStatus, bindTelegram, unbindTelegram,
  fetchPushStatus, muteNotifications, unmuteNotifications,
  checkUsername, setUsername,
  fetchAdminAccessList, setUserMessengerAccess,
  approveAdminUser, deleteAdminUser,
} from "@/api/dashboard";
import { isOnline } from "@/lib/presence";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useTaxonomy, PALETTE_LIST } from "@/lib/useTaxonomy";
import { OrgsPanel } from "@/components/OrgsPanel";
import { ORGS_KEY, fetchMyOrgs } from "@/api/orgs";


function roleLabel(role: string, isSuperuser: boolean): string {
  if (role === "owner") return "Собственник";
  if (role === "admin") return "Администратор";
  if (role === "head_op") return "Руководитель ОП";
  if (role === "op_manager") return "Менеджер ОП";
  if (role === "kc_manager") return "Менеджер КЦ";
  if (role === "assistant") return "Ассистент";
  if (role === "field_engineer") return "Выездной инженер";
  if (isSuperuser) return "Администратор";
  return role || "Менеджер";
}

const DEPARTMENT_LABELS: Record<string, string> = {
  admin: "Администрация",
  sales: "Отдел продаж",
  production: "Производство",
  marketing: "Маркетинг",
  finance: "Финансы",
  supply: "Отдел снабжения",
};

function deptLabels(depts: string[] | undefined | null): string {
  if (!depts || depts.length === 0) return "—";
  return depts.map(d => DEPARTMENT_LABELS[d] || d).join(", ");
}


type Tab = "main" | "appearance" | "settings" | "access" | "orgs";

export function ProfileSelfModal({ onClose, initialTab }: { onClose: () => void; initialTab?: Tab }) {
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState<Tab>(initialTab ?? "main");
  // Организации (03.10.2026): цифра на вкладке — заявки, которые ждут меня как владельца.
  const orgsQ = useQuery({ queryKey: ORGS_KEY, queryFn: fetchMyOrgs, staleTime: 30_000, enabled: !!user });
  const orgPending = orgsQ.data?.pending_total ?? 0;
  const isAdmin = !!user?.is_superuser || user?.role === "admin" || user?.role === "owner";

  // Esc → close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  if (!user) return null;

  return createPortal(
    <div className="self-profile-modal chat-redesign" onClick={onClose}>
      <div className="self-profile-card" onClick={(e) => e.stopPropagation()}>
        <header className="self-profile-header">
          <div className="self-profile-tabs">
            <button className={`self-profile-tab${tab === "main" ? " active" : ""}`} onClick={() => setTab("main")}>Основное</button>
            {/* BrixChat: оформление brixcrm закреплено — вкладки нет (04.10.2026) */}
            {!/(^|\.)brixchat\.ru$|^chat\.brixcrm\.ru$/.test(window.location.hostname) && (
              <button className={`self-profile-tab${tab === "appearance" ? " active" : ""}`} onClick={() => setTab("appearance")}>Оформление</button>
            )}
            <button className={`self-profile-tab${tab === "settings" ? " active" : ""}`} onClick={() => setTab("settings")}>Настройки</button>
            <button className={`self-profile-tab${tab === "orgs" ? " active" : ""}`} onClick={() => setTab("orgs")}>
              Мои организации{orgPending > 0 && <span className="org-badge">{orgPending}</span>}
            </button>
            {isAdmin && (
              <button className={`self-profile-tab${tab === "access" ? " active" : ""}`} onClick={() => setTab("access")}>Доступ</button>
            )}
          </div>
          <button className="self-profile-close" onClick={onClose} aria-label="Закрыть">
            <Icon name="x" size={16}/>
          </button>
        </header>
        <div className="self-profile-body">
          {tab === "main" && <MainTab user={user} refresh={refresh}/>}
          {tab === "appearance" && <AppearanceTab/>}
          {tab === "settings" && <SettingsTab/>}
          {tab === "orgs" && <OrgsPanel/>}
          {tab === "access" && isAdmin && <AccessTab/>}
        </div>
      </div>
    </div>,
    document.body,
  );
}


function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="sp-field">
      <label className="sp-field-label">{label}</label>
      {children}
      {hint && <div className="sp-field-hint">{hint}</div>}
    </div>
  );
}


function MainTab({ user, refresh }: {
  user: NonNullable<ReturnType<typeof useAuth>["user"]>;
  refresh: () => Promise<void>;
}) {
  const [displayName, setDisplayName] = useState(user.display_name ?? "");
  const [bio, setBio] = useState(user.bio ?? "");
  const [phone, setPhone] = useState((user as { phone?: string | null }).phone ?? "");
  const [birthday, setBirthday] = useState<string>(
    (user as { birthday?: string | null }).birthday
      ? `2000-${(user as { birthday?: string | null }).birthday}`
      : ""
  );
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDisplayName(user.display_name ?? "");
    setBio(user.bio ?? "");
    setPhone((user as { phone?: string | null }).phone ?? "");
    const bday = (user as { birthday?: string | null }).birthday;
    setBirthday(bday ? `2000-${bday}` : "");
  }, [user.display_name, user.bio, (user as { phone?: string | null }).phone, (user as { birthday?: string | null }).birthday]);

  const avatarMut = useMutation({
    mutationFn: (file: File) => uploadMyAvatar(file),
    onSuccess: async () => { setError(null); await refresh(); },
    onError: (e: Error) => setError(e.message || "Не удалось загрузить аватар"),
  });
  const deleteAvatarMut = useMutation({
    mutationFn: () => deleteMyAvatar(),
    onSuccess: async () => { setError(null); await refresh(); },
  });
  const profileMut = useMutation({
    mutationFn: (patch: { display_name: string; bio: string | null; birthday: string | null; phone: string | null }) =>
      updateMyProfile(patch),
    onSuccess: async () => { setError(null); setSavedAt(Date.now()); await refresh(); },
    onError: (e: Error) => setError(e.message || "Не удалось сохранить"),
  });

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { setError("Только изображения"); return; }
    if (f.size > 2 * 1024 * 1024) { setError("Файл больше 2 МБ"); return; }
    avatarMut.mutate(f);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const name = displayName.trim();
    if (!name) { setError("Имя не может быть пустым"); return; }
    profileMut.mutate({
      display_name: name,
      bio: bio.trim() || null,
      birthday: birthday ? birthday.slice(5) : null,
      phone: phone.trim() || null,
    });
  }

  const role = roleLabel(user.role || "op_manager", user.is_superuser);
  const online = isOnline(user.last_seen_at);
  const userBday = (user as { birthday?: string | null }).birthday ?? null;
  const userPhone = (user as { phone?: string | null }).phone ?? null;
  const dirty = displayName.trim() !== (user.display_name ?? "")
    || (bio.trim() || null) !== (user.bio ?? null)
    || (birthday ? birthday.slice(5) : null) !== userBday
    || (phone.trim() || null) !== userPhone;

  return (
    <>
      <div className="sp-avatar-row">
        <div style={{ position: "relative", flexShrink: 0 }}>
          <Avatar name={displayName || "Я"} userId={user.id}
            avatarFilename={user.avatar_filename} color="#4fae4e" size={88} online={online}/>
          {avatarMut.isPending && <div className="sp-avatar-overlay">…</div>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="sp-avatar-actions">
            <input type="file" ref={fileInputRef} accept="image/*" hidden onChange={onPickFile}/>
            <button type="button" className="sp-btn-primary"
              onClick={() => fileInputRef.current?.click()} disabled={avatarMut.isPending}>
              <Icon name="image" size={12}/> Загрузить аватар
            </button>
            {user.avatar_filename && (
              <button type="button" className="sp-btn-ghost"
                onClick={() => deleteAvatarMut.mutate()} disabled={deleteAvatarMut.isPending}>
                <Icon name="trash" size={12}/> Удалить
              </button>
            )}
          </div>
          <div className="sp-hint">JPG, PNG, WebP или GIF. До 2 МБ.<br/>Квадратное соотношение — оптимально.</div>
        </div>
      </div>

      <div className="sp-divider"/>

      <form onSubmit={onSubmit} className="sp-form">
        <Field label="Отображаемое имя">
          <input type="text" className="sp-input"
            value={displayName} onChange={(e) => setDisplayName(e.target.value)}
            maxLength={120} placeholder="Как вас будут видеть в чате"/>
        </Field>

        {user.role !== "external" && (<>
        <Field label="Отдел">
          <div className="sp-readonly">{deptLabels(user.departments)}</div>
        </Field>

        <Field label="Должность">
          <div className="sp-readonly">{role}</div>
        </Field>
        </>)}

        <UsernameField user={user} refresh={refresh}/>

        <Field label="Номер телефона">
          <input type="tel" className="sp-input"
            value={phone} onChange={(e) => setPhone(e.target.value)}
            maxLength={32} placeholder="+7 ..."/>
        </Field>

        <Field label="День рождения" hint="Год не отображается у других участников">
          <input type="date" className="sp-input"
            value={birthday} onChange={(e) => setBirthday(e.target.value)}
            style={{ maxWidth: 200 }}/>
        </Field>

        <Field label="Описание профиля">
          <textarea className="sp-input"
            value={bio} onChange={(e) => setBio(e.target.value)}
            maxLength={2000} placeholder="Зона ответственности, контакты, предпочтения по связи…"
            rows={4}
            style={{ resize: "vertical", fontFamily: "inherit" }}/>
        </Field>

        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 6 }}>
          <button type="submit" className="sp-btn-primary"
            disabled={profileMut.isPending || !dirty}>
            {profileMut.isPending ? "…" : "Сохранить"}
          </button>
          {savedAt && Date.now() - savedAt < 3000 && (
            <span style={{ color: "var(--tg-accent, #4fae4e)", fontSize: 12 }}>
              <Icon name="check" size={12}/> Сохранено
            </span>
          )}
        </div>
      </form>

      {error && <div className="sp-error">{error}</div>}
    </>
  );
}


function UsernameField({ user, refresh }: {
  user: NonNullable<ReturnType<typeof useAuth>["user"]>;
  refresh: () => Promise<void>;
}) {
  const initial = (user as { username?: string | null }).username ?? "";
  const [val, setVal] = useState(initial);
  const [status, setStatus] = useState<{ ok: boolean; reason?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => { setVal(initial); }, [initial]);

  // Debounced availability check
  useEffect(() => {
    const v = val.trim().replace(/^@/, "");
    if (!v) { setStatus(null); return; }
    if (v === initial) { setStatus({ ok: true }); return; }
    const t = window.setTimeout(async () => {
      try {
        const r = await checkUsername(v);
        setStatus({ ok: r.available && r.valid, reason: r.reason ?? undefined });
      } catch { setStatus(null); }
    }, 350);
    return () => window.clearTimeout(t);
  }, [val, initial]);

  async function save() {
    const v = val.trim().replace(/^@/, "");
    if (busy) return;
    setBusy(true);
    try {
      await setUsername(v || null);
      await refresh();
      setSavedAt(Date.now());
    } catch (e) {
      const msg = (e as Error).message || "";
      setStatus({ ok: false, reason: msg.includes("409") ? "Уже занят" : msg || "Ошибка" });
    } finally { setBusy(false); }
  }

  const canSave = !busy && val.trim().replace(/^@/, "") !== initial && (status?.ok !== false);

  return (
    <div className="sp-field">
      <label className="sp-field-label">Username (как в TG)</label>
      <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
        <label className="sp-input sp-input-prefix" style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", padding: "0 0 0 12px", gap: 0 }}>
          <span style={{ color: "rgba(0,0,0,0.45)", fontSize: 14 }}>@</span>
          <input type="text"
            value={val.replace(/^@/, "")}
            onChange={(e) => setVal(e.target.value.replace(/[^a-zA-Z0-9_]/g, "").slice(0, 32))}
            placeholder="username"
            maxLength={32}
            style={{
              flex: 1, minWidth: 0,
              border: "none", background: "transparent", outline: "none",
              padding: "10px 12px 10px 2px", fontSize: 14, fontFamily: "inherit",
              color: "#0a0a0a",
            }}/>
        </label>
        <button type="button" className="sp-btn-primary"
          onClick={save} disabled={!canSave}>
          {busy ? "…" : "Сохранить"}
        </button>
      </div>
      <div className="sp-field-hint" style={{
        color: status?.ok === false ? "#c62828" : status?.ok ? "var(--tg-accent, #4fae4e)" : undefined,
      }}>
        {savedAt && Date.now() - savedAt < 3000 ? "Сохранено"
          : status?.ok === false ? (status.reason || "Недоступен")
          : status?.ok ? "Свободен"
          : "3–32 символа: латиница, цифры, _. Уникальный."}
      </div>
    </div>
  );
}


const DEPARTMENTS_LIST = [
  { key: "sales",      label: "Отдел продаж",     default: "green" },
  { key: "production", label: "Производство",     default: "orange" },
  { key: "marketing",  label: "Маркетинг",        default: "pink" },
  { key: "admin",      label: "Администрация",    default: "purple" },
  { key: "finance",    label: "Финансы",          default: "blue" },
  { key: "supply",     label: "Отдел снабжения",  default: "teal" },
] as const;
const DEPT_COLOR_PALETTE = [
  { key: "green",   bg: "#dcedc8", fg: "#33691e" },
  { key: "blue",    bg: "#e3f2fd", fg: "#1565c0" },
  { key: "purple",  bg: "#ede7f6", fg: "#4527a0" },
  { key: "pink",    bg: "#fce4ec", fg: "#ad1457" },
  { key: "orange",  bg: "#ffe0b2", fg: "#b35400" },
  { key: "red",     bg: "#ffcdd2", fg: "#b71c1c" },
  { key: "teal",    bg: "#b2dfdb", fg: "#00695c" },
  { key: "gray",    bg: "#eceff1", fg: "#455a64" },
  { key: "brown",   bg: "#d7ccc8", fg: "#4e342e" },
  { key: "indigo",  bg: "#c5cae9", fg: "#283593" },
  { key: "lime",    bg: "#f0f4c3", fg: "#827717" },
  { key: "ochre",   bg: "#fff9c4", fg: "#9e7c00" },
  { key: "magenta", bg: "#f8bbd0", fg: "#880e4f" },
  { key: "slate",   bg: "#cfd8dc", fg: "#37474f" },
  { key: "mint",    bg: "#c8e6c9", fg: "#1b5e20" },
  { key: "lilac",   bg: "#e1bee7", fg: "#6a1b9a" },
  { key: "navy",    bg: "#bbdefb", fg: "#0d47a1" },
  { key: "coral",   bg: "#ffccbc", fg: "#bf360c" },
] as const;

const ROLES_LIST = [
  { key: "owner",            label: "Собственник",       default: "brown" },
  { key: "admin",            label: "Администратор",     default: "indigo" },
  { key: "head_op",          label: "Руководитель ОП",   default: "navy" },
  { key: "op_manager",       label: "Менеджер ОП",       default: "green" },
  { key: "kc_manager",       label: "Менеджер КЦ",       default: "teal" },
  { key: "assistant",        label: "Ассистент",         default: "lilac" },
  { key: "field_engineer",   label: "Выездной инженер",  default: "ochre" },
  { key: "engineer",         label: "Инженер",           default: "slate" },
  { key: "foreman",          label: "Бригадир",          default: "magenta" },
  { key: "builder",          label: "Строитель",         default: "coral" },
  { key: "warehouse_keeper", label: "Кладовщик",         default: "brown" },
  { key: "accountant",       label: "Бухгалтер",         default: "indigo" },
] as const;

/** Применить цвета отделов. Приоритет: localMap (per-device) → fallbackMap
 *  (серверный taxonomy_departments.default_color) → хардкод-default. */
export function applyDeptColors(map: Record<string, string>, fallback: Record<string, string> = {}) {
  // Список ключей = объединение хардкода + всех ключей из map и fallback (поддержка кастомных отделов)
  const allKeys = new Set<string>([
    ...DEPARTMENTS_LIST.map(d => d.key),
    ...Object.keys(map),
    ...Object.keys(fallback),
  ]);
  const hardcoded = Object.fromEntries(DEPARTMENTS_LIST.map(d => [d.key, d.default]));
  for (const key of allKeys) {
    const colorKey = map[key] || fallback[key] || hardcoded[key];
    if (!colorKey) continue;
    const palette = DEPT_COLOR_PALETTE.find(p => p.key === colorKey);
    if (!palette) continue;
    document.documentElement.style.setProperty(`--dept-${key}-bg`, palette.bg);
    document.documentElement.style.setProperty(`--dept-${key}-fg`, palette.fg);
  }
}

export function applyRoleColors(map: Record<string, string>, fallback: Record<string, string> = {}) {
  const allKeys = new Set<string>([
    ...ROLES_LIST.map(r => r.key),
    ...Object.keys(map),
    ...Object.keys(fallback),
  ]);
  const hardcoded = Object.fromEntries(ROLES_LIST.map(r => [r.key, r.default]));
  for (const key of allKeys) {
    const colorKey = map[key] || fallback[key] || hardcoded[key];
    if (!colorKey) continue;
    const palette = DEPT_COLOR_PALETTE.find(p => p.key === colorKey);
    if (!palette) continue;
    document.documentElement.style.setProperty(`--role-${key}-bg`, palette.bg);
    document.documentElement.style.setProperty(`--role-${key}-fg`, palette.fg);
  }
}

export function loadDeptColorsLS(): Record<string, string> { return loadDeptColors(); }
export function loadRoleColorsLS(): Record<string, string> { return loadRoleColors(); }

function loadRoleColors(): Record<string, string> {
  try {
    const raw = localStorage.getItem("mng-role-colors");
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, string>;
  } catch { return {}; }
}

function loadDeptColors(): Record<string, string> {
  try {
    const raw = localStorage.getItem("mng-dept-colors");
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, string>;
  } catch { return {}; }
}

// Apply on module load
if (typeof window !== "undefined") {
  applyDeptColors(loadDeptColors());
  applyRoleColors(loadRoleColors());
}


function AppearanceTab() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_superuser || user?.role === "admin" || user?.role === "owner";

  const [accent, setAccent] = useState<string>(() => {
    if (/(^|\.)brixchat\.ru$|^chat\.brixcrm\.ru$/.test(window.location.hostname)) return "brix";   // BrixChat: оформление brixcrm закреплено
    try { return localStorage.getItem("mng-accent") || "green"; } catch { return "green"; }
  });
  const [bg, setBg] = useState<string>(() => {
    if (/(^|\.)brixchat\.ru$|^chat\.brixcrm\.ru$/.test(window.location.hostname)) return "brix";
    try { return localStorage.getItem("mng-bg") || "green"; } catch { return "green"; }
  });
  const [deptColors, setDeptColors] = useState<Record<string, string>>(() => loadDeptColors());
  const [roleColors, setRoleColors] = useState<Record<string, string>>(() => loadRoleColors());
  const taxonomyQ = useTaxonomy();
  const taxDepartments = taxonomyQ.data?.departments ?? DEPARTMENTS_LIST;
  const taxRoles = taxonomyQ.data?.roles ?? ROLES_LIST;

  // Debounced sync to server — после любого изменения настроек шлём всё
  // одним PUT'ом. Нужно для синхронизации настроек между устройствами
  // (десктоп ↔ телефон). Source of truth: localStorage (write-through),
  // server — для cross-device load при логине.
  const syncTimerRef = useRef<number | null>(null);
  useEffect(() => {
    // BrixChat: акцент и фон здесь закреплены — не отправляем, иначе затрём настройки мессенджера КБ
    // (сервер заменяет настройки целиком).
    if (/(^|\.)brixchat\.ru$|^chat\.brixcrm\.ru$/.test(window.location.hostname)) return;
    if (syncTimerRef.current) window.clearTimeout(syncTimerRef.current);
    syncTimerRef.current = window.setTimeout(() => {
      const payload = { accent, bg, deptColors, roleColors };
      fetch("/api/profiles/me/ui-prefs", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prefs: payload }),
      }).catch(() => { /* offline-tolerant */ });
    }, 800);
    return () => { if (syncTimerRef.current) window.clearTimeout(syncTimerRef.current); };
  }, [accent, bg, deptColors, roleColors]);

  useEffect(() => {
    try { localStorage.setItem("mng-accent", accent); } catch { /* ignore */ }
    document.documentElement.setAttribute("data-accent", accent);
    const el = document.querySelector(".chat-redesign:not(.self-profile-modal)") as HTMLElement | null;
    if (el) el.setAttribute("data-accent", accent);
  }, [accent]);

  useEffect(() => {
    try { localStorage.setItem("mng-bg", bg); } catch { /* ignore */ }
    document.documentElement.setAttribute("data-bg", bg);
    const el = document.querySelector(".chat-redesign:not(.self-profile-modal)") as HTMLElement | null;
    if (el) el.setAttribute("data-bg", bg);
  }, [bg]);

  useEffect(() => {
    try { localStorage.setItem("mng-dept-colors", JSON.stringify(deptColors)); } catch { /* ignore */ }
    applyDeptColors(deptColors);
  }, [deptColors]);

  useEffect(() => {
    try { localStorage.setItem("mng-role-colors", JSON.stringify(roleColors)); } catch { /* ignore */ }
    applyRoleColors(roleColors);
  }, [roleColors]);

  // Когда taxonomy подгрузилась — применяем CSS-vars и для новых ключей,
  // которых нет в хардкодных DEPARTMENTS_LIST/ROLES_LIST.
  useEffect(() => {
    if (!taxonomyQ.data) return;
    const tax = taxonomyQ.data;
    const dc = loadDeptColors();
    const rc = loadRoleColors();
    for (const d of tax.departments) {
      const colorKey = dc[d.key] || d.default_color;
      const palette = PALETTE_LIST.find(p => p.key === colorKey);
      if (!palette) continue;
      document.documentElement.style.setProperty(`--dept-${d.key}-bg`, palette.bg);
      document.documentElement.style.setProperty(`--dept-${d.key}-fg`, palette.fg);
    }
    for (const r of tax.roles) {
      const colorKey = rc[r.key] || r.default_color;
      const palette = PALETTE_LIST.find(p => p.key === colorKey);
      if (!palette) continue;
      document.documentElement.style.setProperty(`--role-${r.key}-bg`, palette.bg);
      document.documentElement.style.setProperty(`--role-${r.key}-fg`, palette.fg);
    }
  }, [taxonomyQ.data]);

  function setDeptColor(dept: string, colorKey: string) {
    setDeptColors(prev => ({ ...prev, [dept]: colorKey }));
  }
  function setRoleColor(role: string, colorKey: string) {
    setRoleColors(prev => ({ ...prev, [role]: colorKey }));
  }

  return (
    <>
      <div className="sp-section">
        <div className="sp-section-label">Цвет акцента</div>
        <div className="mng-rp-colors">
          {(["green", "blue", "purple", "teal", "orange", "pink", "red", "indigo", "graphite"] as const).map(c => (
            <button key={c} type="button"
              className={`mng-rp-color is-${c}${accent === c ? " active" : ""}`}
              onClick={() => setAccent(c)} title={c}/>
          ))}
        </div>
      </div>

      <div className="sp-divider"/>

      <div className="sp-section">
        <div className="sp-section-label">Фон чата</div>
        <div className="mng-rp-bgs">
          {(["green", "blue", "purple", "beige", "gray", "mint", "peach", "lavender", "sky", "white"] as const).map(b => (
            <button key={b} type="button"
              className={`mng-rp-bg is-${b}${bg === b ? " active" : ""}`}
              onClick={() => setBg(b)} title={b}/>
          ))}
        </div>
      </div>

      {isAdmin && (
        <>
          <div className="sp-divider"/>
          <div className="sp-section">
            <div className="sp-section-label">Цвета этикеток отделов</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {taxDepartments.map(d => {
                const dKey = d.key as string;
                const dDefault = ("default" in d ? d.default : (d as { default_color: string }).default_color) as string;
                const current = deptColors[dKey] || dDefault;
                return (
                  <div key={dKey} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ flex: "0 0 140px", fontSize: 13, color: "#0a0a0a" }}>{d.label}</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {PALETTE_LIST.map(p => (
                        <button key={p.key} type="button"
                          onClick={() => setDeptColor(dKey, p.key)}
                          title={p.key}
                          style={{
                            width: 26, height: 26, borderRadius: "50%",
                            background: p.bg,
                            border: current === p.key ? "2px solid #0a0a0a" : "2px solid transparent",
                            cursor: "pointer",
                            padding: 0,
                          }}/>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="sp-divider"/>
          <div className="sp-section">
            <div className="sp-section-label">Цвета этикеток должностей</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {taxRoles.map(r => {
                const rKey = r.key as string;
                const rDefault = ("default" in r ? r.default : (r as { default_color: string }).default_color) as string;
                const current = roleColors[rKey] || rDefault;
                return (
                  <div key={rKey} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ flex: "0 0 160px", fontSize: 13, color: "#0a0a0a" }}>{r.label}</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {PALETTE_LIST.map(p => (
                        <button key={p.key} type="button"
                          onClick={() => setRoleColor(rKey, p.key)}
                          title={p.key}
                          style={{
                            width: 26, height: 26, borderRadius: "50%",
                            background: p.bg,
                            border: current === p.key ? "2px solid #0a0a0a" : "2px solid transparent",
                            cursor: "pointer",
                            padding: 0,
                          }}/>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}


function SettingsTab() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_superuser || user?.role === "admin" || user?.role === "owner";
  return (
    <>
      <NotificationsBlock/>
      <div className="sp-divider"/>
      <TelegramBlock/>
      {isAdmin && (
        <>
          <div className="sp-divider"/>
          <DeletedVisibilityBlock/>
        </>
      )}
      <div className="sp-divider"/>
      <ResetCacheBlock/>
      <div className="sp-divider"/>
      <LogoutBlock/>
    </>
  );
}

function LogoutBlock() {
  const { logout } = useAuth();
  const [busy, setBusy] = useState(false);
  async function doLogout() {
    if (busy) return;
    if (!window.confirm("Выйти из аккаунта?")) return;
    setBusy(true);
    try {
      await logout();
      // После logout — редирект на логин на текущем origin'е (messenger или dashboard)
      window.location.href = "/login";
    } catch (e) {
      console.warn("logout failed", e);
      setBusy(false);
    }
  }
  return (
    <div className="sp-section">
      <div className="sp-section-title">Выход из аккаунта</div>
      <div className="sp-section-sub" style={{ marginBottom: 12 }}>
        Завершить сессию на этом устройстве. На других устройствах сессия сохранится.
      </div>
      <button type="button" className="sp-btn-danger" onClick={doLogout} disabled={busy}>
        {busy ? "…" : "Выйти"}
      </button>
    </div>
  );
}

function DeletedVisibilityBlock() {
  const [show, setShow] = useState<boolean>(() => {
    try { return localStorage.getItem("kb-show-deleted") !== "0"; }
    catch { return true; }
  });
  function toggle() {
    const next = !show;
    setShow(next);
    try { localStorage.setItem("kb-show-deleted", next ? "1" : "0"); } catch { /* ignore */ }
    // Перезагрузка не нужна — ChatPage перечитает при следующем рендере.
    // Чтобы изменение применилось сразу — можно отправить event и ChatPage его поймает.
    document.dispatchEvent(new CustomEvent("kb-show-deleted-changed", { detail: { show: next } }));
  }
  return (
    <div className="sp-section">
      <div className="sp-section-title">Видимость удалённых сообщений</div>
      <div className="sp-section-sub" style={{ marginBottom: 12 }}>
        Показывать плейсхолдер «сообщение удалено» в ленте, чтобы не терять контекст. Только для админов. Настройка локальная — на этом устройстве.
      </div>
      <button type="button"
        className={show ? "sp-btn-ghost" : "sp-btn-primary"}
        onClick={toggle}>
        {show ? "Скрыть удалённые" : "Показывать удалённые"}
      </button>
    </div>
  );
}


function NotificationsBlock() {
  const qc = useQueryClient();
  const push = usePushNotifications();
  const pushStatusQ = useQuery({
    queryKey: ["push-status"], queryFn: fetchPushStatus, staleTime: 30_000,
  });
  const notifEnabled = pushStatusQ.data ? !pushStatusQ.data.muted : true;
  const isPushSubscribed = push.state.status === "subscribed";
  const isOn = isPushSubscribed && notifEnabled;

  const muteMut = useMutation({
    mutationFn: muteNotifications,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["push-status"] }),
  });
  const unmuteMut = useMutation({
    mutationFn: unmuteNotifications,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["push-status"] }),
  });

  async function toggle() {
    if (isOn) {
      try { muteMut.mutate(); } catch { /* ignore */ }
      try { await push.unsubscribe(); } catch (e) { console.warn(e); }
    } else {
      if (!isPushSubscribed) { const ok = await push.subscribe(); if (!ok) return; }
      try { unmuteMut.mutate(); } catch { /* ignore */ }
    }
  }

  let statusText: string;
  if (push.state.status === "unsupported") statusText = "Браузер не поддерживает push";
  else if (push.state.status === "denied") statusText = "Разрешение отклонено в браузере";
  else if (push.state.status === "subscribed") statusText = notifEnabled ? "Уведомления приходят" : "Тихий режим включён";
  else statusText = "Уведомления выключены";

  const disabled = push.busy || muteMut.isPending || unmuteMut.isPending
    || push.state.status === "unsupported" || push.state.status === "denied";
  return (
    <div className="sp-section">
      <div className="sp-section-title">Уведомления</div>
      <div className="sp-section-sub" style={{ marginBottom: 12 }}>{statusText}</div>
      <button type="button"
        className={isOn ? "sp-btn-ghost" : "sp-btn-primary"}
        onClick={toggle} disabled={disabled}>
        {isOn ? "Выключить уведомления" : "Включить уведомления"}
      </button>
    </div>
  );
}


function TelegramBlock() {
  const qc = useQueryClient();
  const tgQ = useQuery({
    queryKey: ["tg-status"], queryFn: fetchTelegramStatus, staleTime: 30_000,
  });
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const bindMut = useMutation({
    mutationFn: (c: string) => bindTelegram(c),
    onSuccess: () => { setCode(""); setError(null); qc.invalidateQueries({ queryKey: ["tg-status"] }); },
    onError: (e: Error) => setError(e.message || "Не удалось привязать"),
  });
  const unbindMut = useMutation({
    mutationFn: () => unbindTelegram(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tg-status"] }),
  });

  return (
    <div className="sp-section">
      <div className="sp-section-title">Telegram</div>
      {tgQ.data?.bound ? (
        <>
          <div className="sp-section-sub">
            Привязан: {tgQ.data.telegram_username ? `@${tgQ.data.telegram_username}` : `id ${tgQ.data.telegram_id}`}
          </div>
          <button type="button" className="sp-btn-ghost" style={{ marginTop: 10 }}
            onClick={() => unbindMut.mutate()} disabled={unbindMut.isPending}>
            Отвязать
          </button>
        </>
      ) : (
        <>
          <div className="sp-section-sub">
            Откройте бота <a href="https://t.me/kb_dashboard_bot" target="_blank" rel="noopener noreferrer" style={{ color: "var(--tg-accent, #4fae4e)" }}>@kb_dashboard_bot</a>, отправьте <code>/start</code> и введите 6-значный код:
          </div>
          <form onSubmit={(e) => { e.preventDefault(); const c = code.trim(); if (c) bindMut.mutate(c); }}
                style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <input className="sp-input" type="text" value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={6} placeholder="123456"
              style={{ width: 140, fontFamily: "monospace", letterSpacing: 2, textAlign: "center" }}/>
            <button type="submit" className="sp-btn-primary"
              disabled={bindMut.isPending || code.trim().length < 4}>
              Привязать
            </button>
          </form>
          {error && <div className="sp-error" style={{ marginTop: 8 }}>{error}</div>}
        </>
      )}
    </div>
  );
}


function ResetCacheBlock() {
  const [busy, setBusy] = useState(false);
  async function reset() {
    if (busy) return;
    setBusy(true);
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const r of regs) {
          try { await r.unregister(); } catch { /* ignore */ }
        }
      }
      if (window.caches) {
        const ks = await caches.keys();
        await Promise.all(ks.map(k => caches.delete(k)));
      }
      window.location.reload();
    } catch (e) { console.warn(e); setBusy(false); }
  }
  return (
    <div className="sp-section">
      <div className="sp-section-title">Сбросить кэш PWA</div>
      <div className="sp-section-sub">
        Удаляет Service Worker и кэш браузера, перезагружает приложение со свежим кодом. Подписка на push сохранится после повторного входа.
      </div>
      <button type="button" className="sp-btn-ghost" style={{ marginTop: 10 }}
        onClick={reset} disabled={busy}>
        <Icon name="refresh" size={12}/> {busy ? "…" : "Сбросить кэш"}
      </button>
    </div>
  );
}

// === Admin Access Tab — список юзеров + переключатель доступа к мессенджеру ===
function AccessTab() {
  const qc = useQueryClient();
  const accessQ = useQuery({
    queryKey: ["admin/access"],
    queryFn: fetchAdminAccessList,
    staleTime: 30_000,
  });
  const taxonomyQ = useTaxonomy();
  const toggleMut = useMutation({
    mutationFn: (vars: { userId: string; can: boolean }) => setUserMessengerAccess(vars.userId, vars.can),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin/access"] }),
    onError: (e: Error) => alert(e.message),
  });
  // ВАЖНО: все hooks ДО early return — иначе React #310 (hooks order mismatch).
  const approveMut = useMutation({
    mutationFn: (id: string) => approveAdminUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin/access"] }),
    onError: (e: Error) => alert(e.message),
  });
  const rejectMut = useMutation({
    mutationFn: (id: string) => deleteAdminUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin/access"] }),
    onError: (e: Error) => alert(e.message),
  });
  const [groupBy, setGroupBy] = useState<"role" | "department">("role");
  const [filter, setFilter] = useState<string>("");

  if (accessQ.isLoading) return <div className="sp-section-sub">Загрузка…</div>;
  const allUsers = accessQ.data?.users ?? [];
  // Pending: is_active=false (новые регистрации, ждут одобрения админа)
  const pending = allUsers.filter(u => u.is_active === false);
  const users = allUsers.filter(u => u.is_active !== false);
  const filtered = filter
    ? users.filter(u => (u.display_name || "").toLowerCase().includes(filter.toLowerCase()))
    : users;

  const groups: Record<string, typeof users> = {};
  if (groupBy === "role") {
    for (const u of filtered) {
      const k = u.is_superuser ? "owner" : (u.role || "—");
      (groups[k] = groups[k] || []).push(u);
    }
  } else {
    for (const u of filtered) {
      const ds = u.departments.length > 0 ? u.departments : ["—"];
      for (const d of ds) (groups[d] = groups[d] || []).push(u);
    }
  }

  const taxRoles = taxonomyQ.data?.roles ?? [];
  const taxDepts = taxonomyQ.data?.departments ?? [];
  const groupLabel = (key: string) => {
    if (groupBy === "role") {
      return taxRoles.find(r => r.key === key)?.label || key;
    }
    return taxDepts.find(d => d.key === key)?.label || key;
  };

  const sortedKeys = Object.keys(groups).sort();

  return (
    <>
      <div className="sp-section">
        <div className="sp-section-title">Доступ к мессенджеру</div>
        <div className="sp-section-sub" style={{ marginBottom: 12 }}>
          {filtered.length} {filtered.length === 1 ? "сотрудник" : "сотрудников"}.
          Отключение убирает у юзера возможность зайти в мессенджер.
        </div>

        {/* Pending section: новые регистрации, ждут одобрения админа.
            Показывается всегда сверху если pending.length > 0. */}
        {pending.length > 0 && (
          <div style={{
            marginBottom: 18, padding: 12,
            background: "#fff5e8", border: "1px solid #f6c179", borderRadius: 10,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#a85e00", marginBottom: 8 }}>
              ⏳ Заявки на доступ · {pending.length}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {pending.map(u => (
                <div key={u.id} style={{
                  display: "flex", alignItems: "center", gap: 12, padding: "10px 12px",
                  background: "#fff", borderRadius: 8,
                }}>
                  <Avatar name={u.display_name || "?"} userId={u.id}
                    avatarFilename={u.avatar_filename} size={36} color="#f6a623"/>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: "#0a0a0a" }}>
                      {u.display_name || u.email || "—"}
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(0,0,0,0.55)" }}>
                      {u.email || "—"}
                      {u.created_at && (<>{" · "}зарегистрирован {new Date(u.created_at).toLocaleDateString("ru")}</>)}
                    </div>
                  </div>
                  <button type="button"
                    onClick={() => approveMut.mutate(u.id)}
                    disabled={approveMut.isPending || rejectMut.isPending}
                    className="sp-btn-primary"
                    style={{ flexShrink: 0 }}>
                    ✓ Одобрить
                  </button>
                  <button type="button"
                    onClick={() => {
                      if (confirm(`Отклонить заявку ${u.display_name || u.email}?`)) rejectMut.mutate(u.id);
                    }}
                    disabled={approveMut.isPending || rejectMut.isPending}
                    className="sp-btn-ghost"
                    style={{ flexShrink: 0 }}>
                    ✕ Отклонить
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
          <input className="sp-input" type="search" placeholder="Поиск…"
            value={filter} onChange={(e) => setFilter(e.target.value)}
            style={{ flex: 1, minWidth: 160, maxWidth: 300 }}/>
          <div style={{ display: "flex", gap: 4 }}>
            <button type="button"
              className={groupBy === "role" ? "sp-btn-primary" : "sp-btn-ghost"}
              onClick={() => setGroupBy("role")}>По должности</button>
            <button type="button"
              className={groupBy === "department" ? "sp-btn-primary" : "sp-btn-ghost"}
              onClick={() => setGroupBy("department")}>По отделу</button>
          </div>
        </div>

        {sortedKeys.map(k => (
          <div key={k} style={{ marginBottom: 18 }}>
            <div style={{
              fontSize: 12, fontWeight: 600, color: "rgba(0,0,0,0.5)",
              textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6,
            }}>
              {groupLabel(k)} <span style={{ color: "rgba(0,0,0,0.35)", fontWeight: 400 }}>· {groups[k].length}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {groups[k].map(u => (
                <AccessRow key={u.id} user={u}
                  onToggle={(can) => toggleMut.mutate({ userId: u.id, can })}
                  disabled={toggleMut.isPending}/>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function AccessRow({ user, onToggle, disabled }: {
  user: import("@/api/dashboard").AccessRow;
  onToggle: (can: boolean) => void;
  disabled: boolean;
}) {
  const pushOk = user.push_subscriptions > 0 && !user.push_muted;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12, padding: "10px 12px",
      background: "rgba(0,0,0,0.02)", borderRadius: 10,
    }}>
      <Avatar name={user.display_name || "?"} userId={user.id}
        avatarFilename={user.avatar_filename} size={36} color="#4fae4e"/>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: "#0a0a0a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {user.display_name || "—"}
        </div>
        <div style={{ fontSize: 12, color: "rgba(0,0,0,0.55)" }}>
          {user.topic_count > 0 ? `${user.topic_count} ${user.topic_count === 1 ? "чат" : "чатов"}` : "Не в чатах"}
          {" · "}
          <span style={{ color: pushOk ? "#2e7d32" : "rgba(0,0,0,0.45)" }}>
            {pushOk ? "🔔 уведомления вкл" : "🔕 уведомления выкл"}
          </span>
        </div>
      </div>
      <button type="button"
        onClick={() => onToggle(!user.can_access_messenger)}
        disabled={disabled}
        className={user.can_access_messenger ? "sp-btn-ghost" : "sp-btn-primary"}
        style={{ flexShrink: 0 }}>
        {user.can_access_messenger ? "Отключить" : "Включить"}
      </button>
    </div>
  );
}

